import { brokers } from "@/data/brokers";

export interface BrokerComparisonPreview {
  slug: string;
  brokerName: string;
  brokerLogo: string;
  subtitle: string;
  battleTag: string;
  icon: string;
  brokerColor: string;
  xmWins: string[];
  rivalWins: string[];
  quickStats: { label: string; xm: string; rival: string; xmWins: boolean }[];
  verdict: string;
}

export interface BrokerIntro {
  name: string;
  logo: string;
  tagline: string;
  description: string;
  founded: string;
  headquarters: string;
  regulation: string[];
  minDeposit: string;
  maxLeverage: string;
  platforms: string[];
  strengths: string[];
  color: string;
  slug: string;
  website: string;
}

export const brokerIntroductions: BrokerIntro[] = [
  {
    name: "XM",
    logo: "/images/xm.jpg",
    tagline: "The Bonus Empire",
    description: "Founded in 2009, XM has grown into one of the world's most recognized retail forex brokers, serving over 10 million clients across 190+ countries. Known for its industry-leading bonus programs and ultra-low $5 minimum deposit, XM makes forex trading accessible to everyone — from complete beginners testing the waters to seasoned pros managing multi-million dollar portfolios.",
    founded: "2009",
    headquarters: "Cyprus (Limassol)",
    regulation: ["CySEC (Cyprus)", "ASIC (Australia)", "FSC (Belize)", "DFSA (Dubai)"],
    minDeposit: "$5",
    maxLeverage: "1:1000",
    platforms: ["MT4", "MT5", "WebTrader", "XM App"],
    strengths: [
      "$30 no-deposit bonus — start trading with zero risk",
      "100% deposit bonus up to $500 on first deposit",
      "XMP loyalty points redeemable for cash on every trade",
      "Multilingual support in 30+ languages, 24/5"
    ],
    color: "#7C3AED",
    slug: "/xm-review",
    website: brokers.xm.href
  },
  {
    name: "Tickmill",
    logo: "/images/TM.jpg",
    tagline: "The ECN Purist",
    description: "Tickmill has carved out a reputation as the go-to broker for cost-obsessed traders who refuse to compromise on execution quality. With FCA regulation backing its operations and Raw ECN accounts offering spreads from 0.0 pips, Tickmill attracts high-volume traders who need institutional-grade pricing without the institutional minimums. No bonuses, no gimmicks — just razor-thin spreads and bulletproof execution.",
    founded: "2014",
    headquarters: "UK (London) / Seychelles",
    regulation: ["FCA (UK)", "CySEC (Cyprus)", "FSA (Seychelles)", "FSCA (South Africa)"],
    minDeposit: "$100",
    maxLeverage: "1:1000",
    platforms: ["MT4", "MT5", "TradingView", "WebTrader"],
    strengths: [
      "Raw ECN spreads from 0.0 pips — among the lowest in the industry",
      "FCA regulation with FSCS protection up to £85,000",
      "True ECN execution with no dealing desk intervention",
      "$3/side commission on Raw — competitive industry rate"
    ],
    color: "#DC2626",
    slug: "/compare/xm-vs-tickmill",
    website: brokers.tickmill.href
  },
  {
    name: "FxPro",
    logo: "/images/fxpro.jpg",
    tagline: "The Institutional Standard",
    description: "FxPro brings institutional-grade trading infrastructure to retail clients, with FCA regulation and cTrader platform support that serious traders demand. Unlike bonus-driven competitors, FxPro competes on execution quality, platform diversity, and capital security. With over 15 years in the game and a spotless regulatory record, FxPro is the safe choice for traders who prioritize fund protection over promotional gimmicks.",
    founded: "2006",
    headquarters: "UK (London) / Cyprus",
    regulation: ["FCA (UK)", "CySEC (Cyprus)", "FSCA (South Africa)", "SCB (Bahamas)"],
    minDeposit: "$100",
    maxLeverage: "1:500",
    platforms: ["MT4", "MT5", "cTrader", "FxPro Edge"],
    strengths: [
      "FCA-regulated with top-tier investor protection since 2006",
      "cTrader platform — industry-leading execution and charting",
      "No dealing desk — pure agency model with no conflicts",
      "Institutional-grade liquidity from multiple Tier-1 banks"
    ],
    color: "#F59E0B",
    slug: "/compare/xm-vs-fxpro",
    website: brokers.fxpro.href
  },
  {
    name: "IC Markets",
    logo: "/images/ic.jpg",
    tagline: "The Algo Trader's Paradise",
    description: "IC Markets is the undisputed king of the algo and EA trading world. With raw spreads from 0.0 pips, deep liquidity from 25+ providers, and native cTrader support alongside MT4/MT5, IC Markets has built its entire infrastructure around the needs of automated traders. If you run EAs, grid strategies, or high-frequency scalping systems, this is where your setup will perform at its absolute best.",
    founded: "2007",
    headquarters: "Australia (Sydney) / Seychelles",
    regulation: ["ASIC (Australia)", "CySEC (Cyprus)", "FSA (Seychelles)"],
    minDeposit: "$200",
    maxLeverage: "1:500",
    platforms: ["MT4", "MT5", "cTrader", "TradingView"],
    strengths: [
      "Raw spreads from 0.0 pips on EUR/USD — EA grids thrive here",
      "Deep liquidity pool from 25+ Tier-1 providers",
      "MQL5 VPS built-in for seamless automated trading",
      "TradingView integration for advanced web-based charting"
    ],
    color: "#EF4444",
    slug: "/compare/xm-vs-icmarkets",
    website: brokers.icmarkets.href
  },
  {
    name: "Axi",
    logo: "/images/axi.jpg",
    tagline: "The AI-Enhanced Contender",
    description: "Axi (formerly AxiTrader) sets itself apart through technology — specifically, its exclusive PsyQuation AI analytics platform that analyzes your trading behavior and provides personalized insights to improve performance. With MT4 NexGen offering enhanced charting tools and sentiment indicators out of the box, Axi appeals to traders who believe data-driven decision making gives them an edge over the competition.",
    founded: "2007",
    headquarters: "Australia (Sydney) / UK",
    regulation: ["ASIC (Australia)", "FCA (UK)", "DFSA (Dubai)"],
    minDeposit: "$0",
    maxLeverage: "1:500",
    platforms: ["MT4 NexGen", "MT4", "WebTrader"],
    strengths: [
      "PsyQuation AI — personalized trading analytics and insights",
      "MT4 NexGen with advanced sentiment indicators built-in",
      "Autochartist automated pattern recognition included",
      "$0 minimum deposit — no barrier to getting started"
    ],
    color: "#3B82F6",
    slug: "/compare/xm-vs-axi",
    website: brokers.axi.href
  },
  {
    name: "FXTM",
    logo: "/images/fx.jpg",
    tagline: "The Loyalty Merchant",
    description: "FXTM (ForexTime) built its brand on rewarding committed traders. While XM throws bonuses at new accounts, FXTM's tiered loyalty program grows with your trading volume — the more you trade, the better your perks. Add in a dedicated copy trading platform, mobile-first innovation with the FXTM Trader App, and strong emerging-market presence, and you have a broker designed for long-term relationship building.",
    founded: "2011",
    headquarters: "Cyprus (Limassol) / Mauritius",
    regulation: ["CySEC (Cyprus)", "FSC (Mauritius)", "FCA (UK)", "FSCA (South Africa)"],
    minDeposit: "$50",
    maxLeverage: "1:2000",
    platforms: ["MT4", "MT5", "FXTM Trader App"],
    strengths: [
      "Tiered loyalty program — higher volume unlocks better perks",
      "FXTM Invest copy trading — follow verified strategy managers",
      "1:2000 max leverage — highest in this comparison group",
      "FXTM Trader App with built-in signals and sentiment analysis"
    ],
    color: "#8B5CF6",
    slug: "/compare/xm-vs-fxtm",
    website: brokers.fxtm.href
  }
];

export const comparisonPreviews: BrokerComparisonPreview[] = [
  {
    slug: "/compare/xm-vs-tickmill",
    brokerName: "Tickmill",
    brokerLogo: "/images/TM.jpg",
    subtitle: "Bonus empire meets ultra-low ECN pricing. XM's generous promotions and low entry barrier versus Tickmill's FCA-regulated Raw account with industry-lowest spreads — who wins?",
    battleTag: "Bonus vs Pure ECN Cost",
    icon: "ri-swap-line",
    brokerColor: "#DC2626",
    xmWins: [
      "$30 no-deposit bonus + 100% welcome bonus — trade with house money first",
      "$5 minimum deposit — the lowest barrier to entry in the industry",
      "99.35% execution rate in under 1 second, virtually zero requotes"
    ],
    rivalWins: [
      "Raw account with EUR/USD avg 0.1 pip spread — among the lowest all-in costs globally",
      "UK FCA regulation with FSCS protection up to £85,000 — top-tier fund security",
      "True ECN execution with no dealing desk intervention — pure market pricing"
    ],
    quickStats: [
      { label: "Min Deposit", xm: "$5", rival: "$100", xmWins: true },
      { label: "Max Leverage", xm: "1:1000", rival: "1:1000", xmWins: false },
      { label: "Raw Spread", xm: "0.6 pips", rival: "0.1 pips", xmWins: false },
      { label: "Bonuses", xm: "$30 + 100%", rival: "None", xmWins: true }
    ],
    verdict: "Cost-conscious high-volume traders choose Tickmill for ultra-low ECN pricing and FCA security. Bonus hunters and beginners with small capital choose XM for the $5 entry and $30 no-deposit bonus."
  },
  {
    slug: "/compare/xm-vs-fxpro",
    brokerName: "FxPro",
    brokerLogo: "/images/fxpro.jpg",
    subtitle: "A veteran FCA-regulated broker with premium execution and cTrader support. XM's community-driven approach meets FxPro's institutional-grade infrastructure.",
    battleTag: "Traditional vs Modern",
    icon: "ri-building-4-line",
    brokerColor: "#F59E0B",
    xmWins: [
      "Aggressive bonus program with no-deposit bonus and loyalty points on every trade",
      "Lower minimum deposit at $5 — accessible to traders at every level",
      "Free VPS for EA traders with generous qualifying requirements"
    ],
    rivalWins: [
      "FCA regulation with top-tier investor protection and compensation schemes",
      "cTrader platform support — industry-leading for precision execution",
      "Institutional-grade liquidity for large-volume traders with no dealing desk"
    ],
    quickStats: [
      { label: "Regulation", xm: "ASIC/CySEC", rival: "FCA/CySEC", xmWins: false },
      { label: "Platforms", xm: "MT4/MT5", rival: "MT4/MT5/cTrader", xmWins: false },
      { label: "Min Deposit", xm: "$5", rival: "$100", xmWins: true },
      { label: "Trading Tools", xm: "VPS + XMP", rival: "cTrader + Algo", xmWins: true }
    ],
    verdict: "Institutional traders and cTrader fans lean FxPro. Bonus-driven retail traders and beginners find XM more welcoming."
  },
  {
    slug: "/compare/xm-vs-icmarkets",
    brokerName: "IC Markets",
    brokerLogo: "/images/ic.jpg",
    subtitle: "Australia's raw spread king meets XM's bonus empire. IC Markets dominates the EA and algo trading scene — does XM's execution hold up?",
    battleTag: "Manual vs EA Trading",
    icon: "ri-robot-3-line",
    brokerColor: "#EF4444",
    xmWins: [
      "$30 no-deposit bonus lets you test strategies risk-free before committing capital",
      "Loyalty points (XMP) effectively reduce spread costs on every trade",
      "Better suited for manual traders who value support and educational resources"
    ],
    rivalWins: [
      "Raw Spread from 0.0 pips with low commission — EA grid strategies thrive here",
      "Deep liquidity pool with multiple Tier-1 providers for consistent fills",
      "MQL5 VPS built-in for MT4/MT5 users with seamless EA deployment"
    ],
    quickStats: [
      { label: "Raw Spread", xm: "from 0.6", rival: "from 0.0", xmWins: false },
      { label: "Commission", xm: "$0 (Ultra Low)", rival: "$7/lot", xmWins: true },
      { label: "EA Support", xm: "VPS + MT4/5", rival: "VPS + cTrader", xmWins: false },
      { label: "Beginner Fit", xm: "9.5/10", rival: "6/10", xmWins: true }
    ],
    verdict: "EA and algo traders swear by IC Markets' raw spreads. Manual traders and beginners get more value from XM's ecosystem and bonuses."
  },
  {
    slug: "/compare/xm-vs-axi",
    brokerName: "Axi",
    brokerLogo: "/images/axi.jpg",
    subtitle: "Two brokers, both offering MT4 NexGen and strong execution. Axi's psychometric trading tools versus XM's bonus-first strategy — which gives you the edge?",
    battleTag: "Platform Performance Test",
    icon: "ri-speed-up-line",
    brokerColor: "#3B82F6",
    xmWins: [
      "100% deposit bonus effectively doubles your trading capital from day one",
      "Free VPS with lower qualifying requirements for automated strategies",
      "Multi-regulated with broader global coverage and localized support"
    ],
    rivalWins: [
      "PsyQuation AI-powered trading analytics for smarter decision making",
      "MT4 NexGen with advanced sentiment indicators and enhanced charting",
      "Autochartist integration for automated pattern recognition and trade signals"
    ],
    quickStats: [
      { label: "Execution", xm: "99.35% &lt;1s", rival: "Ultra-fast", xmWins: true },
      { label: "AI Tools", xm: "Basic", rival: "PsyQuation AI", xmWins: false },
      { label: "Bonus", xm: "100% up to $500", rival: "None", xmWins: true },
      { label: "Regulation", xm: "Multi-jurisdiction", rival: "ASIC/FCA", xmWins: true }
    ],
    verdict: "AI-driven traders who love analytics pick Axi. Capital-conscious traders who want bonuses and stability stick with XM."
  },
  {
    slug: "/compare/xm-vs-fxtm",
    brokerName: "FXTM",
    brokerLogo: "/images/fx.jpg",
    subtitle: "Two bonus-heavy brokers go head-to-head. XM's $30 no-deposit bonus versus FXTM's Loyalty Program — who rewards traders better in the long run?",
    battleTag: "Loyalty & Bonus Comparison",
    icon: "ri-vip-crown-2-line",
    brokerColor: "#8B5CF6",
    xmWins: [
      "$30 no-deposit bonus — start trading with real money before depositing anything",
      "XMP loyalty points on every trade, redeemable for cash or bonus credit",
      "Lower minimum deposit at $5 with 100% welcome bonus doubling your first deposit"
    ],
    rivalWins: [
      "FXTM Trader App with built-in signals and sentiment analysis for mobile traders",
      "Invest Copy Trading program with transparent strategy manager profiles",
      "Loyalty program scales with trading volume — higher tiers unlock better perks"
    ],
    quickStats: [
      { label: "No-Deposit Bonus", xm: "$30", rival: "None", xmWins: true },
      { label: "Welcome Bonus", xm: "100% up to $500", rival: "Varies by region", xmWins: true },
      { label: "Loyalty", xm: "XMP Points", rival: "Tiered Program", xmWins: false },
      { label: "Copy Trading", xm: "MQL5 Signal", rival: "Dedicated App", xmWins: false }
    ],
    verdict: "XM wins on pure bonus value and accessibility. FXTM's copy trading and tiered loyalty appeal to committed long-term traders."
  }
];

export interface CompareStat {
  value: string;
  label: string;
  icon: string;
}

export const compareStats: CompareStat[] = [
  { value: "5", label: "Broker Comparisons", icon: "ri-scales-3-line" },
  { value: "50+", label: "Data Points Analyzed", icon: "ri-database-2-line" },
  { value: "8", label: "Comparison Dimensions", icon: "ri-layout-grid-line" },
  { value: "100%", label: "Independent & Unbiased", icon: "ri-shield-check-line" }
];

export interface CompareMethod {
  icon: string;
  title: string;
  description: string;
}

export const compareMethods: CompareMethod[] = [
  {
    icon: "ri-test-tube-line",
    title: "Live Account Testing",
    description: "We open real accounts with each broker using our own funds. No demo accounts. No simulated environments. Every spread, execution speed, and withdrawal time is measured from actual trading conditions."
  },
  {
    icon: "ri-scales-line",
    title: "Weighted Multi-Dimension Scoring",
    description: "Each broker is scored across 8 dimensions: cost, execution speed, regulation, platform quality, bonuses, customer support, deposit/withdrawal experience, and educational resources. Scores are weighted by what matters most to traders."
  },
  {
    icon: "ri-user-voice-line",
    title: "Community Feedback Integration",
    description: "We aggregate and verify real user reviews from Trustpilot, ForexPeaceArmy, and trading forums. Every claim is cross-referenced against our own testing before being included in our analysis."
  },
  {
    icon: "ri-refresh-line",
    title: "Quarterly Data Refresh",
    description: "Broker conditions change. Spreads widen, bonuses expire, regulations shift. We re-test every broker every quarter and update all comparison data to reflect the latest real-world conditions."
  }
];

export const faqData = [
  {
    q: "Why should I compare brokers before opening an account?",
    a: "Each broker has fundamentally different strengths. XM excels at bonuses and beginner-friendly features. Tickmill dominates with ultra-low ECN spreads and FCA-grade regulation. IC Markets is built for EA traders. Choosing the wrong broker for your trading style can cost you thousands in unnecessary spreads, commissions, and missed opportunities. A 30-minute comparison now saves years of frustration."
  },
  {
    q: "Is XM always the best choice on this site?",
    a: "Absolutely not. While this site focuses on XM (because it's one of the most popular brokers globally), our comparisons are 100% unbiased. We clearly show where competitors beat XM — and in many categories, they do. Tickmill wins on ultra-low ECN costs and FCA regulation. IC Markets wins on EA infrastructure. Our goal is matching you with the right broker, not selling you on XM."
  },
  {
    q: "How do I choose between so many brokers?",
    a: "Start with your trading style. Scalping? Look at spreads and commission. Swing trading? Focus on swap fees and regulation. Small capital? Prioritize low minimum deposits and bonuses. Each comparison page includes real-world scenario breakdowns that show exactly how each broker performs in your specific situation. If you're still unsure, try the interactive quiz on the XM vs Tickmill page for a personalized recommendation."
  },
  {
    q: "Can I open accounts with multiple brokers?",
    a: "Yes, and many professional traders do. There's no rule against having accounts with XM, Tickmill, and IC Markets simultaneously. Many traders use XM for bonus-boosted swing trading, Tickmill for ultra-low ECN scalping, and IC Markets for running EAs — getting the best of all worlds."
  }
];